import { useMemo, useState, useEffect } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';

import { Button, Divider } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { DateGDT } from '@/types';
import { EnumTeamContext, ResponseRankingHistoric } from '@/types/team';
import { cn, formatearNumero } from '@/utils';

interface Props {
  matchsDaysAvailables: DateGDT[];
  responseRanking?: ResponseRankingHistoric | null;
  lastRanking: {
    dateName?: string;
    matchDayScore?: number;
    gralScore?: number;
    matchDayPosition?: number;
    gralPosition?: number;
  };
  isLoading: boolean;
  context: EnumTeamContext;
  onSubmit: (date: string) => void;
}

export const BoxRankingTeam = ({
  lastRanking,
  context,
  matchsDaysAvailables,
  responseRanking,
  isLoading,
  onSubmit,
}: Props) => {
  const initialValueState = {
    counter: matchsDaysAvailables.length - 1 || 0,
    disablingPrev: matchsDaysAvailables.length <= 1,
    disablingNext: true,
  };
  const [stateDate, setStateDate] = useState(initialValueState);
  const { isMobile, isDesktop } = useIsDesktop();

  const teamContext = [EnumTeamContext.Team11, EnumTeamContext.Team5].includes(
    context
  );
  const scoreContext = [
    EnumTeamContext.Score11,
    EnumTeamContext.Score5,
  ].includes(context);

  // MOUNT RANKING DATA - INITIAL VALUE STATE
  useEffect(() => {
    if (scoreContext) {
      const date = String(
        Object.values(matchsDaysAvailables)[stateDate.counter]?.orden
      );
      onSubmit(date);
    }
    // UNMOUNT RANKING - RESET INITIAL VALUE STATE
    return () => {
      setStateDate(initialValueState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context]);

  const handlePrevMatch = () => {
    if (stateDate.disablingPrev) return;
    setStateDate({
      ...stateDate,
      disablingNext: false,
      disablingPrev: stateDate.counter <= 1,
      counter: !(stateDate.counter === 0)
        ? stateDate.counter - 1
        : stateDate.counter,
    });
    const date = String(
      Object.values(matchsDaysAvailables)[stateDate.counter]?.orden - 1
    );
    onSubmit(date);
  };

  const handleNextMatch = () => {
    if (stateDate.disablingNext) return null;
    setStateDate({
      ...stateDate,
      disablingPrev: false,
      disablingNext: stateDate.counter + 2 === matchsDaysAvailables.length,
      counter: !(stateDate.counter === matchsDaysAvailables.length - 1)
        ? stateDate.counter + 1
        : stateDate.counter,
    });
    const date = String(
      Object.values(matchsDaysAvailables)[stateDate.counter]?.orden + 1
    );
    onSubmit(date);
  };

  const texts = {
    textScoreDate: isMobile ? 'PUNTOS DE LA FECHA' : 'FECHA',
    textPositionDate: isMobile ? 'POS. FECHA' : 'FECHA',
    textScoreTour: isMobile ? 'PUNTOS DEL TORNEO' : 'TORNEO',
    textPositionTour: isMobile ? 'POS. TORNEO' : 'TORNEO',
  };

  const classesTitle = 'text-gdt-gray-4 text-2xs lg:text-xs lg:text-bold ';

  const getDataRanking = useMemo(() => {
    if (teamContext) {
      return {
        dateName: lastRanking.dateName || '-',
        matchDayScore: lastRanking.matchDayScore || '-',
        matchDayPosition: lastRanking.matchDayPosition || '-',
        gralScore: lastRanking.gralScore || '-',
        gralPosition: lastRanking.gralPosition || '-',
      };
    } else {
      return {
        dateName:
          Object.values(matchsDaysAvailables)[stateDate.counter]?.nombre ||
          lastRanking.dateName ||
          '-',
        matchDayScore: responseRanking?.infoPuntajes.puntosFecha || '-',
        gralScore: responseRanking?.infoPuntajes.puntosGeneral || '-',
        matchDayPosition: responseRanking?.infoPuntajes.posicionFecha || '-',
        gralPosition: responseRanking?.infoPuntajes.posicionGeneral || '-',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    context,
    lastRanking,
    matchsDaysAvailables,
    stateDate.counter,
    responseRanking,
  ]);

  return (
    <div>
      <div className="flex items-center bg-transparent lg:rounded-tl-lg lg:rounded-tr-lg lg:bg-gdt-blue-xl2 h-8 lg:h-[3.3rem] mb-4 lg:mb-0 justify-center mt-4">
        {scoreContext && (
          <Button
            className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
              'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                stateDate.disablingPrev,
            })}
            disabled={isLoading}
            type="button"
            variant="primary"
            onClick={handlePrevMatch}
          >
            <IoIosArrowBack className="m-auto" size={19} />
          </Button>
        )}
        <p className="text-gdt-blue text-base lg:text-lg font-bold mx-5 min-w-[76px]">
          {getDataRanking?.dateName}
        </p>
        {scoreContext && (
          <Button
            className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
              'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                stateDate.disablingNext,
            })}
            disabled={isLoading}
            type="button"
            variant="primary"
            onClick={handleNextMatch}
          >
            <IoIosArrowForward className="m-auto" size={19} />
          </Button>
        )}
      </div>
      {isDesktop && (
        <div className="bg-white rounded-lg lg:rounded-tl-none lg:rounded-tr-none py-4 lg:pb-8 lg:pt-2">
          <div className="p-4 lg:px-8">
            <strong className="hidden lg:block text-gdt-gray lg:text-base">
              Puntos
            </strong>

            {/* GRID DE 2 COLUMNAS PARA ALINEAR DATE / TOUR */}
            <div className="grid grid-cols-2 gap-4 lg:mt-6">
              {/* FECHA (PUNTOS) */}
              <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <p className={classesTitle}>{texts.textScoreDate}</p>
                <strong className="text-gdt-blue-header text-lg lg:text-2xl lg:text-gdt-gray">
                  {formatearNumero(Number(getDataRanking?.matchDayScore))}
                </strong>
              </div>

              {/* TORNEO (PUNTOS) */}
              <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <p className={classesTitle}>{texts.textScoreTour}</p>
                <strong className="text-gdt-blue-header text-lg lg:text-2xl lg:text-gdt-gray">
                  {formatearNumero(Number(getDataRanking?.gralScore))}
                </strong>
              </div>
            </div>
          </div>

          <Divider
            className="bg-gdt-gray-6 lg:bg-gdt-gray-5 lg:mt-3"
            type="horizontal"
          />

          <div className="px-4 lg:px-8 pt-2 lg:pt-6">
            <strong className="hidden lg:block lg:text-base text-gdt-gray">
              Posición
            </strong>

            {/* GRID DE 2 COLUMNAS PARA ALINEAR FECHA / TORNEO IGUAL QUE ARRIBA */}
            <div className="grid grid-cols-2 gap-4 lg:mt-6">
              {/* FECHA (POSICIÓN) */}
              <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <p className={cn('pr-1', classesTitle)}>
                  {texts.textPositionDate}
                </p>
                <strong className="text-sm text-gdt-gray-2 lg:text-xl">
                  {formatearNumero(Number(getDataRanking?.matchDayPosition))}
                </strong>
              </div>

              {/* TORNEO (POSICIÓN) */}
              <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
                <p className={cn('pr-1', classesTitle)}>
                  {texts.textPositionTour}
                </p>
                <strong className="text-sm text-gdt-gray-2 lg:text-xl">
                  {formatearNumero(Number(getDataRanking?.gralPosition))}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div className="bg-white rounded-lg lg:rounded-tl-none lg:rounded-tr-none py-4 lg:pb-8 lg:pt-2">
          <div className="p-4 lg:px-8">
            <strong className="hidden lg:block text-gdt-gray lg:text-base">
              Puntos
            </strong>
            <div className="flex items-center lg:mt-6">
              <div className="flex-1 lg:flex items-center lg:flex-none">
                <p className={classesTitle}>{texts.textScoreDate}</p>
                <strong className="text-gdt-blue-header text-lg lg:text-2xl lg:text-gdt-gray lg:ml-3">
                  {formatearNumero(Number(getDataRanking?.matchDayScore))}
                </strong>
              </div>
              <Divider
                className="bg-gdt-gray-6 h-[2.6rem] lg:hidden"
                type="vertical"
              />
              <div
                className={`flex-1 ${
                  isDesktop && getDataRanking?.matchDayScore === '-'
                    ? 'relative left-20'
                    : isDesktop && getDataRanking?.matchDayScore !== '-'
                    ? 'relative left-16'
                    : 'relative left-6'
                } lg:flex items-center lg:flex-none lg:ml-[.05rem]`}
              >
                <p className={classesTitle}>{texts.textScoreTour}</p>
                <strong className="text-gdt-blue-header text-lg lg:text-2xl lg:text-gdt-gray lg:ml-3">
                  {formatearNumero(Number(getDataRanking?.gralScore))}
                </strong>
              </div>
            </div>
          </div>
          <Divider
            className="bg-gdt-gray-6 lg:bg-gdt-gray-5 lg:mt-3"
            type="horizontal"
          />
          <div className="px-4 lg:px-8 pt-2 lg:pt-6">
            <strong className="hidden lg:block lg:text-base text-gdt-gray">
              Posición
            </strong>
            <div className="flex items-center justify-evenly lg:mt-6">
              <div className="flex-1 lg:flex-col flex items-baseline">
                <p className={cn('pr-1', classesTitle)}>
                  {texts.textPositionDate}
                </p>
                <strong className="text-sm text-gdt-gray-2 lg:text-xl lg:mt-1">
                  {formatearNumero(Number(getDataRanking?.matchDayPosition))}
                </strong>
              </div>
              <Divider
                className="bg-gdt-gray-6 lg:hidden h-[2rem]"
                type="vertical"
              />
              <div className="flex-1 lg:flex-col flex items-baseline relative left-6 lg:left-0">
                <p className={cn('pr-1', classesTitle)}>
                  {texts.textPositionTour}
                </p>
                <strong className="text-sm text-gdt-gray-2 lg:text-xl">
                  {formatearNumero(Number(getDataRanking?.gralPosition))}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
